// src/merch/SaveDialog.jsx
//
// Save project → a private link to keep editing (any device), plus a
// read-only 3D view link to show friends / the coach.

import { href } from "./stores";
import React, { useEffect, useState } from "react";
import { saveDesign, emailDesignLinks } from "./designs";

function CopyRow({ label, url, hint }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  }
  async function share() {
    try { await navigator.share({ title: "My MerchMod design", url }); } catch { /* cancelled */ }
  }
  return (
    <div className="ma-share-row">
      <b>{label}</b>
      <div className="ma-share-url">
        <input readOnly value={url} onFocus={(e) => e.target.select()} />
        <button className="ma-btn" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
        {typeof navigator !== "undefined" && navigator.share && <button className="ma-btn ghost" onClick={share}>Share</button>}
      </div>
      <p className="ma-muted">{hint}</p>
    </div>
  );
}

// Parent's email → we send the links. Marketing is a SEPARATE, unchecked
// opt-in (CASL). No names or ages collected — users may be kids.
function EmailLinks() {
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [state, setState] = useState(null); // null | "sending" | "sent" | error string
  async function send(e) {
    e.preventDefault();
    setState("sending");
    try {
      await emailDesignLinks({ email: email.trim(), marketing });
      setState("sent");
    } catch (err) {
      setState(err.message || "Couldn't send.");
    }
  }
  if (state === "sent") {
    return <div className="ma-note">📬 Sent to <b>{email}</b> — check your inbox (and spam folder).</div>;
  }
  return (
    <form className="ma-share-row ma-email-links" onSubmit={send}>
      <b>Email me my links</b>
      <div className="ma-share-url">
        <input type="email" required placeholder="Parent's email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        <button className="ma-btn primary" disabled={state === "sending"}>{state === "sending" ? "Sending…" : "Send"}</button>
      </div>
      <label className="ma-check">
        <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
        <span>Also send me MerchMod news, new designs and team deals. You can unsubscribe any time.</span>
      </label>
      {state && state !== "sending" && <div className="ma-note error">{state}</div>}
      <p className="ma-muted">
        We only use this email to send your links{marketing ? " and the news you asked for" : ""}. No spam, never sold.{" "}
        <a href={href("/privacy")} target="_blank" rel="noreferrer">Privacy</a>
      </p>
    </form>
  );
}

export default function SaveDialog({ onClose }) {
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    saveDesign().then(setRes).catch((e) => setError(e.message || "Couldn't save."));
  }, []);

  return (
    <div className="ma-modal" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ma-modal-card ma-save" role="dialog" aria-label="Save project">
        <div className="ma-modal-head">
          <b>{res ? "Saved ✓" : error ? "Save failed" : "Saving…"}</b>
          <button className="ma-btn ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {error && <div className="ma-note error">{error}</div>}
        {!res && !error && <p className="ma-muted">Saving your design…</p>}
        {res && (
          <>
            <CopyRow
              label="Keep editing"
              url={res.editUrl}
              hint="Your private link — open it on any device to pick up where you left off. Keep it to yourself: anyone with it can change the design."
            />
            <CopyRow
              label="Show it off (3D view)"
              url={res.viewUrl}
              hint="A look-only link to the 3D stick — share it with family, friends or the coach."
            />
            <EmailLinks />
            <p className="ma-muted">Tip: tap Save again any time — the same links update with your latest changes.</p>
          </>
        )}
      </div>
    </div>
  );
}
